import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'

import { completeAnimatingIn, completeAnimatingOut, completePreloading, saveNavTheme } from '../../reducers/nav'
import { calcComponentState } from '../../fxs/component-utils'
import { fetchApiData } from '../../actions/api'
import { apiResourceEndpoint } from '../../constants/routes'
import { ReactComponent as MarkerStroke } from '../../assets/svgs/line.svg'
import anime from 'animejs'

import AccordionBand from './bands/accordion'
import ImageWithText from './bands/image-with-text'
import TextWithQuote from './bands/text-with-quote'
import MetaracismQuote from './bands/metaracism'
import SideBySide from './bands/side-by-side'
import Wysiwyg from './bands/wysiwyg'
import MailingListSignup from './bands/mailing-list-signup'
import { clearMusicSource } from '../../reducers/sound'
import { Helmet } from 'react-helmet'
import VideoBand from './bands/video-band'
import { ReactComponent as HeaderTrim } from '../../assets/svgs/key-issue-header-trim.svg'
import SecondaryFooter from '../layout/secondary-footer'
import FullWidthImage from './bands/full-width-image'

const mapStateToProps = (state) => {
    let alias = state.nav?.activeUrl || ''
    // no slash on end
    if (alias.endsWith('/')) {
        alias = alias.slice(0, -1)
    }
    // remove leading slash
    if (alias.startsWith('/')) {
        alias = alias.slice(1)
    }
    return {
        alias,
        navState: state.nav,
        content: state.content.find(item => item?.alias.endsWith(alias))
    }
}

const ContentPage = ({ dispatch, navState, content, alias }) => {
    const [scrollPercent, setScrollPercent] = useState(0)
    const [scrolledHalf, setScrolledHalf] = useState(false)
    const [scrolledSeventyFive, setScrolledSeventyFive] = useState(false)
    const [scrolledNinety, setScrolledNinety] = useState(false)

    const handleScroll = () => {
        const appWrapper = document.getElementById('app')
        if (appWrapper) {
            const scrollTop = appWrapper.pageYOffset || appWrapper.scrollTop
            const appHeight = appWrapper.scrollHeight || appWrapper.offsetHeight
            const windowHeight = window.innerHeight || document.documentElement.clientHeight
            const percent = (scrollTop / (appHeight - windowHeight)) * 100
            setScrollPercent(percent)
        }
    }

    useEffect(() => {
        const appWrapper = document.getElementById('app')
        appWrapper.addEventListener('scroll', handleScroll)
        return () => {
            appWrapper.removeEventListener('scroll', handleScroll)
        }
    }, [])
    const componentName = 'Content Page'

    useEffect(() => {
        if (scrollPercent >= 50 && !scrolledHalf) {
            setScrolledHalf(true)
            dispatchGtmScrollEvent(50)
        }
        if (scrollPercent >= 75 && !scrolledSeventyFive) {
            setScrolledSeventyFive(true)
            dispatchGtmScrollEvent(75)
        }
        if (scrollPercent >= 90 && !scrolledNinety) {
            setScrolledNinety(true)
            dispatchGtmScrollEvent(90)
        }
    }, [scrollPercent, scrolledHalf, scrolledSeventyFive, scrolledNinety])

    const componentState = useMemo(() => {
        return calcComponentState(componentName, navState)
    }, [navState, componentName])

    const dispatchGtmScrollEvent = (value) => {
        let dataLayer = window.dataLayer || []
        dataLayer.push({
            event: 'scrollThreshold',
            scrollPercent: value
        })
    }

    useEffect(() => {
        if (componentState === 'PRELOADING') {
            handlePreloadingState()
        } else if (componentState === 'ANIMATING_IN') {
            dispatch(saveNavTheme('white'))
            handleAnimatingInState()
        } else if (componentState === 'ANIMATING_OUT') {
            handleAnimatingOutState()
        }
    }, [componentState])

    useEffect(() => {
        if (componentState === 'ACTIVE' && content) {
            const headerObserver = new IntersectionObserver(
                (entries) => {
                    const isHeaderVisible = entries[0].isIntersecting

                    if (!isHeaderVisible) {
                        dispatch(saveNavTheme('grey'))
                    } else {
                        dispatch(saveNavTheme('white'))
                    }
                },
                {
                    threshold: 0,
                    rootMargin: '-10px 0px 0px 0px'
                }
            )
            const headerElement = document.querySelector('.content-header')
            if (headerElement) {
                headerObserver.observe(headerElement)
            }
            return () => {
                if (headerElement) {
                    headerObserver.unobserve(headerElement)
                }
                headerObserver.disconnect()
            }
        }
    }, [componentState, content, dispatch])

    const handlePreloadingState = async () => {
        dispatch(fetchApiData(apiResourceEndpoint(`content/${alias}`, 'LIST')))
            .then(() => {
                dispatch(completePreloading())
            })
    }

    const handleAnimatingInState = async () => {
        dispatch(clearMusicSource())
        const markerLine = animateLine()
        markerLine.finished.then(() => {
            dispatch(completeAnimatingIn())
        })
    }

    const handleAnimatingOutState = () => {
        dispatch(completeAnimatingOut())
    }

    const animateLine = () => {
        return anime({
            targets: '.title-stroke #marker',
            strokeDashoffset: 0,
            easing: 'easeInQuart',
            duration: 750
        })
    }

    if (!navState || !componentState || !componentName) {
        return null
    }

    // Only check componentState after we know we have all required data
    if (componentState === 'OFF' || componentState === 'PRELOADING') {
        return null
    }

    const renderContentBands = () => {
        let allContent = []
        let videoIndex = 1
        content.contentBands.forEach((band, index) => {
            const { bundle, data } = band
            switch (bundle) {
            case 'wysiwyg':
                allContent.push(<Wysiwyg key={index} content={data} />)
                break
            case 'layout_accordion':
                allContent.push(<AccordionBand key={index} content={data} />)
                break
            case 'metaracism_quote':
                allContent.push(<MetaracismQuote key={index} content={data} />)
                break
            case 'side_by_side':
                allContent.push(<SideBySide key={index} content={data} />)
                break
            case 'image_with_text':
                allContent.push(<ImageWithText key={index} content={data} />)
                break
            case 'full_width_image':
                allContent.push(<FullWidthImage key={index} content={data} />)
                break
            case 'text_with_quote':
                allContent.push(<TextWithQuote key={index} content={data} />)
                break
            case 'mailing_list_form':
                allContent.push(<MailingListSignup key={index} content={data} />)
                break
            case 'video_band':
                allContent.push(<VideoBand key={index} videoIndex={videoIndex} content={band.data} />)
                if (videoIndex < 5) {
                    videoIndex = videoIndex + 1
                } else {
                    videoIndex = 1
                }
                break
            default:
                break
            }
        })
        return <div className="content-bands">{allContent}</div>
    }

    if (!content) {
        return null
    }

    return (
        <>
            <Helmet>
                <title>{content.pageTitle}</title>
                <meta name="description" content={content.metaDescription} />
                <meta property="og:title" content={content.pageTitle} />
                <meta property="og:description" content={content.metaDescription} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={window.location.href} />
                <meta property="og:image" content={content.metaImage?.url} />
            </Helmet>
            <div className="content-page-wrapper">
                <div className="content-header">
                    <div className="container">
                        <div className="title-stroke">
                            <h1>{content.title}</h1>
                            <MarkerStroke />
                        </div>
                    </div>
                    <div className="header-trim">
                        <HeaderTrim />
                    </div>
                </div>
                {renderContentBands()}
                {content && content.footer &&
                    <div className="container" style={{ paddingBottom: '50px' }}>
                        <SecondaryFooter footer={content.footer} />
                    </div>
                }
                <div className="ml-embed" data-form="E2Do8E" />
            </div>
        </>
    )
}

export default connect(mapStateToProps)(ContentPage)
